import { EventLeadType } from '@/lib/enums/EventLeadType'
import { MessageInspirationSourceType } from '@/lib/enums/MessageInspirationSourceType'
import { idSchema } from '@/lib/utils'
import { z } from 'zod/v4'

export const messageIdSchema = z.object({ messageId: idSchema('message') })
export const MAX_EVENT_INTERNAL_NOTE_LENGTH = 10000
export const MAX_EVENT_INTERNAL_NOTES_JSON_LENGTH = 50000

export function getEventInternalNotesJsonLength(
  notes: z.infer<typeof eventInternalNoteSchema>[] | null | undefined
): number {
  return JSON.stringify(notes ?? []).length
}

export const eventInternalNoteSchema = z.object({
  note: z
    .string()
    .min(1, 'Note is required')
    .max(MAX_EVENT_INTERNAL_NOTE_LENGTH, 'Note is too long'),
  timestamp: z.string().min(1, 'Timestamp is required'),
})

const inspirationUrlSourceTypes = new Set<string>([
  MessageInspirationSourceType.PinterestBoard.Value,
  MessageInspirationSourceType.GooglePhotosAlbum.Value,
  MessageInspirationSourceType.IcloudAlbum.Value,
])

export const baseMessageSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120, 'Name is too long'),
  email: z.email('Please enter a valid email'),
  phone: z.string().min(1, 'Phone is required').max(40, 'Phone is too long'),
  message: z.string().min(1, 'Message is required').max(5000, 'Message is too long'),
  is_event: z.boolean(),
  event_type: z.string().optional().nullable(),
  event_type_other: z.string().optional().nullable(),
  event_date: z
    .union([z.iso.date(), z.literal(''), z.null()])
    .optional()
    .nullable(),
  event_location: z.string().max(255, 'Event location is too long').optional().nullable(),
  inspiration_source_type: z.string().optional().nullable(),
  inspiration_source_url: z
    .string()
    .max(2048, 'Inspiration link is too long')
    .optional()
    .nullable(),
  inspiration_media: z
    .array(z.object({ id: z.union([z.number(), z.string()]) }).loose())
    .optional()
    .nullable(),
  internal_notes: z
    .array(eventInternalNoteSchema)
    .refine(
      (notes) => getEventInternalNotesJsonLength(notes) <= MAX_EVENT_INTERNAL_NOTES_JSON_LENGTH,
      {
        message: 'Notes are too long',
      }
    )
    .optional()
    .nullable(),
})

export const messageContactInformationSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120, 'Name is too long'),
  email: z.email('Please enter a valid email'),
  phone: z.string().min(1, 'Phone is required').max(40, 'Phone is too long'),
})

/** The fields both the full and the details-only message schemas validate together. */
type MessageDetailsValue = Pick<
  z.output<typeof baseMessageSchema>,
  | 'is_event'
  | 'event_type'
  | 'event_type_other'
  | 'inspiration_source_type'
  | 'inspiration_source_url'
  | 'inspiration_media'
>

function validateMessageDetails(value: MessageDetailsValue, ctx: z.RefinementCtx) {
  if (value.is_event && !value.event_type?.trim()) {
    ctx.addIssue({
      code: 'custom',
      message: 'Event type is required',
      path: ['event_type'],
    })
  }

  if (
    value.is_event &&
    value.event_type === EventLeadType.Other.Value &&
    !value.event_type_other?.trim()
  ) {
    ctx.addIssue({
      code: 'custom',
      message: 'Other event type is required',
      path: ['event_type_other'],
    })
  }

  if (!value.is_event) {
    return
  }

  const sourceType = value.inspiration_source_type
  if (sourceType === MessageInspirationSourceType.Upload.Value) {
    if (!Array.isArray(value.inspiration_media) || value.inspiration_media.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Upload at least one inspiration photo',
        path: ['inspiration_media'],
      })
    }

    return
  }

  if (
    sourceType &&
    inspirationUrlSourceTypes.has(sourceType) &&
    !value.inspiration_source_url?.trim()
  ) {
    ctx.addIssue({
      code: 'custom',
      message: 'Enter the inspiration link',
      path: ['inspiration_source_url'],
    })
  }
}

export const messageDetailsSchema = baseMessageSchema
  .omit({
    name: true,
    email: true,
    phone: true,
  })
  .superRefine(validateMessageDetails)

export const messageSchema = baseMessageSchema.superRefine(validateMessageDetails)
